import apiClient from './client';
import { Check } from '../types';

export const checksApi = {
  /**
   * List all checks for a client
   */
  listChecks: async (clientId: string): Promise<Check[]> => {
    const response = await apiClient.get<Check[]>(`/clients/${clientId}/checks`);
    return response.data;
  },

  /**
   * Get a specific check
   */
  getCheck: async (clientId: string, checkId: string): Promise<Check> => {
    const response = await apiClient.get<Check>(`/clients/${clientId}/checks/${checkId}`);
    return response.data;
  },

  /**
   * Update a check
   */
  updateCheck: async (clientId: string, checkId: string, data: Partial<Check>): Promise<Check> => {
    const response = await apiClient.put<Check>(`/clients/${clientId}/checks/${checkId}`, data);
    return response.data;
  },

  /**
   * Approve a check
   */
  approveCheck: async (clientId: string, checkId: string): Promise<Check> => {
    const response = await apiClient.post<Check>(`/clients/${clientId}/checks/${checkId}/approve`, {});
    return response.data;
  },

  /**
   * Reject a check
   */
  rejectCheck: async (clientId: string, checkId: string): Promise<Check> => {
    const response = await apiClient.post<Check>(`/clients/${clientId}/checks/${checkId}/reject`);
    return response.data;
  },

  /**
   * Unlock a check (reset to pending status)
   */
  unlockCheck: async (clientId: string, checkId: string): Promise<Check> => {
    const response = await apiClient.post<Check>(`/clients/${clientId}/checks/${checkId}/unlock`);
    return response.data;
  },

  /**
   * Assign a check to a customer
   */
  assignCheckToCustomer: async (
    clientId: string,
    checkId: string,
    customerId: string
  ): Promise<Check> => {
    const response = await apiClient.post<Check>(
      `/clients/${clientId}/checks/${checkId}/assign-customer`,
      { customer_id: customerId }
    );
    return response.data;
  },

  /**
   * Delete a check
   */
  deleteCheck: async (clientId: string, checkId: string): Promise<void> => {
    await apiClient.delete(`/clients/${clientId}/checks/${checkId}`);
  },

  /**
   * Assign a vendor or customer as payee to a check
   * Used by inline payee assignment in IncompletePurchasesTab and ReviewChecksTab
   * @param payeeIsCustomer - When true, assigns customer_id; when false, assigns vendor_id
   */
  assignVendorToCheck: async (
    clientId: string,
    checkId: string,
    vendorId: string | null,
    payeeIsCustomer: boolean = false,
    customerId: string | null = null
  ): Promise<Check> => {
    const response = await apiClient.post<Check>(
      `/clients/${clientId}/checks/${checkId}/assign-vendor`,
      {
        vendor_id: vendorId,
        customer_id: customerId,
        payee_is_customer: payeeIsCustomer
      }
    );
    return response.data;
  },

  /**
   * List all approved checks with customer and vendor info
   * Used by the Review Checks tab
   * @param filter - Optional filter: 'missing_customer', 'missing_vendor', or 'payee_is_customer'
   * @param accountId - Optional account ID to filter by
   */
  listApprovedChecks: async (
    clientId: string,
    filter?: 'missing_customer' | 'missing_vendor' | 'payee_is_customer',
    accountId?: string
  ): Promise<Check[]> => {
    const params: Record<string, string> = {};
    if (filter) params.filter = filter;
    if (accountId) params.account_id = accountId;
    const response = await apiClient.get<Check[]>(
      `/clients/${clientId}/checks/approved`,
      { params }
    );
    return response.data;
  },

  /**
   * Bulk delete duplicate checks
   * Only checks marked as duplicates can be deleted
   */
  bulkDeleteChecks: async (
    clientId: string,
    checkIds: string[]
  ): Promise<{ deleted_count: number }> => {
    const response = await apiClient.delete<{ deleted_count: number }>(
      `/clients/${clientId}/checks/bulk`,
      { data: { check_ids: checkIds } }
    );
    return response.data;
  },
};
